<returning-users>
    <!-- Returning Users Quick Login.
         When mounted as a sidebar next to the login form (props.compact)
         the section is styled as a slim, visually secondary "quick
         sign-in" aside: muted header, lighter card surface, smaller
         avatar / type, and the per-card "last login" line is hidden to
         keep tiles short. The goal is for users to clearly read this
         column as a helpful shortcut, not the main attraction. -->
    <div class="mb-6" if="{state.returningUsers.length > 0}">
        <div class="{props.compact ? 'text-xs font-semibold uppercase tracking-wide text-base-content/80 mb-2' : 'text-sm text-base-content/80 mb-3'}">{locale?.t('returning_welcome')}:</div>

        <div class="space-y-2">
            <div
                each="{user in state.returningUsers}"
                key="{user.userUuid}"
                class="card transition-colors relative group {props.compact ? 'bg-base-100 border border-base-300' : 'bg-base-200'}"
            >
                <div class="card-body {props.compact ? 'p-3' : 'p-4'}">
                    <!-- User Info Row -->
                    <div class="flex items-center justify-between" if="{state.selectedUserId !== user.userUuid}">
                        <button
                            type="button"
                            class="flex items-center gap-3 flex-1 cursor-pointer hover:opacity-80"
                            onclick="{() => handleUserClick(user)}"
                        >
                            <!-- Avatar.
                                 DaisyUI 5 renamed the placeholder modifier
                                 from `avatar placeholder` (two classes) to
                                 `avatar-placeholder` (one hyphenated). The
                                 old syntax silently broke centering: the
                                 .avatar > div { display: block } rule has
                                 higher specificity than a Tailwind .flex
                                 utility, so the inner div was a block box
                                 and the initials anchored to the top-left
                                 corner of the circle. Using the v5 class
                                 lets DaisyUI's own
                                 .avatar-placeholder > div rule apply the
                                 flex centering. -->
                            <div class="avatar avatar-placeholder">
                                <div class="bg-primary text-primary-content rounded-full {props.compact ? 'w-9 h-9' : 'w-12 h-12'}">
                                    <span class="leading-none {props.compact ? 'text-sm font-medium' : 'text-xl'}">{getInitials(user.name)}</span>
                                </div>
                            </div>

                            <!-- User info -->
                            <div class="flex-1 min-w-0">
                                <div class="font-semibold {props.compact ? 'text-sm truncate' : ''}">{user.name}</div>
                                <div class="text-xs text-base-content/80 {props.compact ? 'truncate' : 'text-sm'}">{user.email}</div>
                                <!-- "Last login" hidden in compact mode — too much
                                     vertical noise for a sidebar tile. -->
                                <div if="{!props.compact}" class="text-xs text-base-content/80 mt-1">
                                    {locale?.t('returning_last_login')}: {getTimeSince(user.lastLoginAt)}
                                </div>
                            </div>
                        </button>

                        <!-- Auth method badges & Remove button -->
                        <div class="flex gap-2 items-end">
                            <div class="flex flex-col gap-1">
                                <!-- Compact mode: icon-only "badges" so the
                                     auth method indicator doesn't dominate
                                     the narrow card. Passkeys are literal
                                     cryptographic keys so the key icon is
                                     a better fit than the previously-used
                                     shield; passwords get the padlock icon
                                     to keep the visual distinction clear.
                                     The full text variant still appears in
                                     the default (non-compact) layout. -->
                                <div if="{user.hasPasskey && props.compact}" class="tooltip tooltip-left" data-tip="{locale?.t('returning_passkey')}">
                                    <i class="icon-metalark-key text-primary"></i>
                                </div>
                                <div if="{user.hasPassword && props.compact}" class="tooltip tooltip-left" data-tip="{locale?.t('returning_password')}">
                                    <i class="icon-metalark-lock text-secondary"></i>
                                </div>
                                <div if="{user.hasPasskey && !props.compact}" class="badge badge-primary badge-sm gap-1">
                                    <i class="icon-metalark-key"></i>
                                    {locale?.t('returning_passkey')}
                                </div>
                                <div if="{user.hasPassword && !props.compact}" class="badge badge-secondary badge-sm gap-1">
                                    <i class="icon-metalark-lock"></i>
                                    {locale?.t('returning_password')}
                                </div>
                            </div>
                            <button
                                type="button"
                                class="btn btn-xs opacity-0 group-hover:opacity-100 transition-opacity {state.confirmRemoveId === user.userUuid ? 'btn-error' : 'btn-ghost'}"
                                onclick="{(e) => handleRemoveClick(e, user)}"
                                title="{state.confirmRemoveId === user.userUuid ? locale?.t('returning_remove_tooltip_confirm') : locale?.t('returning_remove_tooltip')}"
                                aria-label="{state.confirmRemoveId === user.userUuid ? locale?.t('returning_remove_tooltip_confirm') : locale?.t('returning_remove_tooltip')}"
                            >
                                <i class="bi {state.confirmRemoveId === user.userUuid ? 'icon-metalark-check-circle' : 'icon-bin'}"></i>
                            </button>
                        </div>
                    </div>

                    <!-- Quick Login Form (when selected) -->
                    <div if="{state.selectedUserId === user.userUuid}" class="space-y-3">
                        <!-- User Info (condensed) with Cancel Button -->
                        <div class="flex items-center gap-3 pb-3 border-b">
                            <!-- See compact-avatar comment above for the
                                 DaisyUI 5 avatar-placeholder rename. -->
                            <div class="avatar avatar-placeholder">
                                <div class="bg-primary text-primary-content rounded-full w-12 h-12">
                                    <span class="text-xl leading-none">{getInitials(user.name)}</span>
                                </div>
                            </div>
                            <div class="flex-1">
                                <div class="font-semibold">{user.name}</div>
                                <div class="text-sm text-base-content/80">{user.email}</div>
                            </div>
                            <button
                                class="btn btn-ghost btn-sm gap-2"
                                onclick="{() => cancelQuickLogin()}"
                                title="{locale?.t('returning_choose_different')}"
                            >
                                <i class="icon-metalark-arrow-left"></i>
                                <span class="hidden sm:inline">{locale?.t('returning_back')}</span>
                            </button>
                        </div>

                        <!-- Error Alert -->
                        <div if="{state.quickLoginError}" class="alert alert-error alert-sm">
                            <i class="icon-metalark-alert-circle"></i>
                            <span class="text-sm">{state.quickLoginError}</span>
                        </div>

                        <!-- Password Input (if user has password) -->
                        <form
                            if="{user.hasPassword}"
                            id="quick-login-form-{user.userUuid}"
                            method="post"
                            onsubmit="{(e) => handleQuickLoginSubmit(e, user)}"
                        >
                            <!-- Hidden email field for password managers -->
                            <input
                                type="email"
                                id="quick-login-email-{user.userUuid}"
                                name="email"
                                value="{user.email}"
                                autocomplete="username"
                                aria-label="{locale?.t('loginform_email')}"
                                class="sr-only"
                                tabindex="-1"
                                readonly
                            />
                            <div class="flex gap-2">
                                <input
                                    type="password"
                                    id="quick-login-password-{user.userUuid}"
                                    name="password"
                                    placeholder="{locale?.t('returning_enter_password')}"
                                    aria-label="{locale?.t('returning_enter_password')}"
                                    class="input input-bordered flex-1"
                                    value="{state.quickLoginPassword}"
                                    oninput="{handlePasswordInput}"
                                    disabled="{state.quickLoginLoading}"
                                    autocomplete="current-password"
                                    data-autofocus="true"
                                    required
                                />
                                <button
                                    type="submit"
                                    id="quick-login-submit-{user.userUuid}"
                                    class="btn btn-primary"
                                    disabled="{state.quickLoginLoading || !state.quickLoginPassword}"
                                >
                                    <span if="{!state.quickLoginLoading}">
                                        <i class="icon-metalark-chevron-right"></i>
                                    </span>
                                    <span if="{state.quickLoginLoading}" class="loading loading-spinner loading-sm"></span>
                                </button>
                            </div>
                        </form>

                        <!-- Passkey Button (if user has passkey) -->
                        <button
                            if="{user.hasPasskey}"
                            class="btn btn-primary btn-block"
                            onclick="{() => handleQuickPasskeyLogin(user)}"
                            disabled="{state.quickLoginLoading}"
                        >
                            <span if="{!state.quickLoginLoading}">
                                <i class="icon-metalark-shield me-2"></i>
                                {locale?.t('returning_signin_passkey')}
                            </span>
                            <span if="{state.quickLoginLoading}" class="loading loading-spinner loading-sm"></span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- "Or sign in with…" divider, shown only in the legacy
             single-column page layout where the login form sits
             directly below this list. login.tag passes hideDivider when
             rendering the form and the users list as side-by-side
             columns — a horizontal divider mid-column makes no sense
             there. -->
        <div if="{!props.hideDivider}" class="divider">{locale?.t('returning_or')}</div>
    </div>


    <script>
        import { ReturningUserManager } from '../../services/returning-user-manager.js';
        import { getAuthService, getPasskeyPromptService } from '../../services/service-loader';

        export default {
            includeTools: false,

            state: {
                returningUsers: [],
                confirmRemoveId: null,
                selectedUserId: null,
                quickLoginPassword: '',
                quickLoginLoading: false,
                quickLoginError: null,
                conditionalAuthActive: false
            },

            onBeforeMount() {
                // Load returning users
                this.loadReturningUsers();
            },

            // Mirrors login-form.tag's helper: read ?returnto=<path> off the
            // hash route and validate it's a same-origin relative path,
            // falling back to the caller's default. Lets accept-invitation
            // and other deep links round-trip the user back to the right
            // page after a returning-user quick login.
            getPostLoginDestination(fallback = '/') {
                try {
                    const hash = window.location.hash || '';
                    const qIdx = hash.indexOf('?');
                    if (qIdx === -1) return fallback;
                    const params = new URLSearchParams(hash.slice(qIdx + 1));
                    const raw = params.get('returnto');
                    if (!raw) return fallback;
                    if (!raw.startsWith('/') || raw.startsWith('//')) return fallback;
                    if (/^[a-z][a-z0-9+.-]*:/i.test(raw)) return fallback;
                    return raw;
                } catch (_) {
                    return fallback;
                }
            },


            onUpdated() {
                // Auto-focus password input when a user is selected
                // But NOT when passkey login is loading (user clicked and we're prompting them)
                if (this.state.selectedUserId && !this.state.quickLoginLoading) {
                    // Use setTimeout to ensure DOM is updated
                    setTimeout(() => {
                        const passwordInput = this.$('input[data-autofocus="true"]');
                        if (passwordInput) {
                            passwordInput.focus();
                        }
                    }, 0);
                }
            },

            loadReturningUsers() {
                const users = ReturningUserManager.getReturningUsers();
                // Extra safety check: filter out any users with blank critical fields
                this.state.returningUsers = users.filter(user =>
                    user.email && user.email.trim() !== '' &&
                    user.name && user.name.trim() !== '' &&
                    user.userUuid && user.userUuid.trim() !== ''
                );
            },

            async handleUserClick(user) {
                // Select user for quick login
                this.state.selectedUserId = user.userUuid;
                this.state.quickLoginPassword = '';
                this.state.quickLoginError = null;
                this.update();
                
                // Notify parent that quick login is active
                this.props.onUserSelected?.();

                // If user has passkey, immediately trigger passkey login
                if (user.hasPasskey) {
                    console.log('[ReturningUsers] User has passkey, triggering immediate passkey login...');
                    await this.handleQuickPasskeyLogin(user);
                }
            },

            cancelQuickLogin() {
                this.state.selectedUserId = null;
                this.state.quickLoginPassword = '';
                this.state.quickLoginError = null;
                this.update();
                
                // Notify parent that quick login was cancelled
                this.props.onQuickLoginCancelled?.();
            },

            handlePasswordInput(e) {
                this.state.quickLoginPassword = e.target.value;
                this.state.quickLoginError = null;
                this.update();
            },

            handleQuickLoginSubmit(e, user) {
                e.preventDefault();
                if (this.state.quickLoginPassword && !this.state.quickLoginLoading) {
                    this.handleQuickLogin(user);
                }
            },

            async handleQuickLogin(user) {
                try {
                    this.state.quickLoginLoading = true;
                    this.state.quickLoginError = null;
                    this.update();

                    console.log('[Quick Login] Attempting login for:', user.email);
                    const authService = await getAuthService();
                    const result = await authService.loginWithPassword(
                        user.email,
                        this.state.quickLoginPassword
                    );

                    console.log('[Quick Login] Login result:', result.success ? 'success' : 'failed', result.error || '');

                    if (result.success) {
                        console.log('[Quick Login] Login successful');
                        
                        // Notify parent of successful login (so they can stop conditional auth)
                        this.props.onLoginSuccess?.();
                        
                        // Show passkey registration prompt if user doesn't already have one
                        setTimeout(async () => {
                            console.log('[Quick Login] Checking if passkey prompt should be shown...');
                            
                            if (!user.hasPasskey) {
                                console.log('[Quick Login] Attempting to show passkey prompt via service...');
                                const PasskeyPromptService = await getPasskeyPromptService();
                                const promptShown = await PasskeyPromptService.show();
                                console.log('[Quick Login] Prompt shown:', promptShown);
                                
                                // Only redirect if prompt not shown
                                if (!promptShown) {
                                    console.log('[Quick Login] Prompt not shown, redirecting to workspace selection...');
                                    window.location.href = this.getPostLoginDestination('/#select-workspace');
                                }
                            } else {
                                // User already has passkey, just redirect
                                console.log('[Quick Login] User already has passkey, redirecting...');
                                window.location.href = this.getPostLoginDestination('/#select-workspace');
                            }
                        }, 300);
                    } else {
                        this.state.quickLoginLoading = false;
                        this.state.quickLoginError = result.error;
                        this.update();
                    }
                } catch (error) {
                    console.error('[Quick Login] Unexpected error:', error);
                    this.state.quickLoginLoading = false;
                    this.state.quickLoginError = this.locale?.t('returning_error_unexpected');
                    this.update();
                }
            },

            async handleQuickPasskeyLogin(user) {
                this.state.quickLoginLoading = true;
                this.state.quickLoginError = null;
                this.update();

                try {
                    console.log('[ReturningUsers] Starting passkey login for', user.email);
                    const authService = await getAuthService();
                    const result = await authService.loginWithPasskey(user.email);

                    if (result.success) {
                        console.log('[ReturningUsers] Passkey login successful');
                        
                        // Notify parent of successful login (so they can stop conditional auth)
                        this.props.onLoginSuccess?.();
                        
                        // Full page reload to ensure cookie is picked up
                        // Keep loading state true to prevent button re-enabling
                        const dest = this.getPostLoginDestination();
                        setTimeout(() => {
                            window.location.href = dest;
                        }, 300);
                    } else {
                        // Check if this was a user cancellation
                        const isCancellation = result.error && (
                            result.error.toLowerCase().includes('cancel') ||
                            result.error.toLowerCase().includes('dismiss') ||
                            result.error.toLowerCase().includes('abort') ||
                            result.error.toLowerCase().includes('not allowed')
                        );

                        if (isCancellation && user.hasPassword) {
                            // User cancelled - silently fall back to password field
                            console.log('[ReturningUsers] User cancelled passkey, falling back to password field');
                            this.state.quickLoginLoading = false;
                            this.state.quickLoginError = null; // Don't show error for cancellation
                            this.update();
                            
                            // Auto-focus password field
                            setTimeout(() => {
                                const passwordInput = this.$('input[data-autofocus="true"]');
                                if (passwordInput) {
                                    passwordInput.focus();
                                }
                            }, 100);
                        } else {
                            // Real error - show it
                            console.error('[ReturningUsers] Passkey login failed:', result.error);
                            this.state.quickLoginLoading = false;
                            this.state.quickLoginError = result.error;
                            this.update();
                        }
                    }
                } catch (error) {
                    console.error('[ReturningUsers] Unexpected passkey error:', error);
                    this.state.quickLoginLoading = false;
                    this.state.quickLoginError = this.locale?.t('returning_error_passkey_failed');
                    this.update();
                }
            },

            handleRemoveClick(e, user) {
                // Stop event propagation to prevent triggering card click
                e.stopPropagation();
                
                if (this.state.confirmRemoveId === user.userUuid) {
                    // Second click - actually remove the user
                    ReturningUserManager.removeUser(user.userUuid, false);
                    this.state.confirmRemoveId = null;
                    this.loadReturningUsers();
                    this.update();
                } else {
                    // First click - set confirm state
                    this.state.confirmRemoveId = user.userUuid;
                    this.update();
                    
                    // Reset confirm state after 3 seconds
                    setTimeout(() => {
                        if (this.state.confirmRemoveId === user.userUuid) {
                            this.state.confirmRemoveId = null;
                            this.update();
                        }
                    }, 3000);
                }
            },

            getInitials(name) {
                if (!name) return '?';
                const parts = name.split(' ');
                if (parts.length >= 2) {
                    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
                }
                return name.substring(0, 2).toUpperCase();
            },

            getTimeSince(timestamp) {
                return ReturningUserManager.getTimeSinceLogin(timestamp);
            }
        }
    </script>
</returning-users>